import { type GetObjectsOptions, type SearchOptions } from '@algolia/client-search';
import { type RequestOptions } from '@algolia/transporter';
import { type SearchClient, type SearchIndex } from 'algoliasearch';

import { createAlgoliaClient } from '@libs/algolia';
import type { ProductInterfaceAlgolia } from '@libs/common-types';
import { logger } from '@libs/core';

import { getAlgoliaRefererHeader } from '../utils/getAlgoliaRefererHeader';

export class AlgoliaProductService {
    readonly client: SearchClient;
    index: SearchIndex;

    constructor(indexName: string) {
        this.client = createAlgoliaClient({ admin: true });
        this.index = this.client.initIndex(indexName);
    }

    async fetchAllProducts(searchOptions: SearchOptions & RequestOptions = {}): Promise<ProductInterfaceAlgolia[]> {
        const products: ProductInterfaceAlgolia[] = [];
        try {
            await this.index.browseObjects({
                ...searchOptions,
                headers: {
                    ...searchOptions.headers,
                    'x-petm-algolia-caller': 'web:algolia-lib:fetchAllProducts',
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
                batch: batch => {
                    products.push(...(batch as ProductInterfaceAlgolia[]));
                },
            });
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`[fetchAllProducts] Error fetching all products: ${error.message}`);
            } else {
                logger.error('[fetchAllProducts] Error:', error);
                throw new Error(`[fetchAllProducts] Unknown error occurred`);
            }
        }
        return products;
    }

    async fetchProductsByIds(
        ids: string[],
        requestOptions?: RequestOptions & GetObjectsOptions
    ): Promise<ProductInterfaceAlgolia[]> {
        try {
            logger.info('IDS', ids);
            const { results } = await this.index.getObjects<ProductInterfaceAlgolia>(ids, {
                ...requestOptions,
                headers: {
                    ...requestOptions?.headers,
                    'x-petm-algolia-caller': 'web:algolia-lib:fetchProductsByIds',
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
            });
            return results.filter((product): product is ProductInterfaceAlgolia => product !== null);
        } catch (error) {
            if (error instanceof Error) {
                logger.error(`[fetchProductsByIds] Error fetching products by IDs: ${error.message}`, {
                    error,
                });
                throw new Error(`[fetchProductsByIds] Error fetching products by IDs: ${error.message}`);
            } else {
                logger.error('[fetchProductsByIds] Error:', error);
                throw new Error(`[fetchProductsByIds] Unknown error occurred`);
            }
        }
    }

    async updateProducts(products: ProductInterfaceAlgolia[]): Promise<void> {
        try {
            await this.index.partialUpdateObjects(products, {
                createIfNotExists: true,
                headers: {
                    'x-petm-algolia-caller': 'web:algolia-lib:updateProducts',
                    'x-petm-algolia-referer': getAlgoliaRefererHeader(),
                },
            });
        } catch (error) {
            if (error instanceof Error) {
                logger.error(`[updateProducts] Error updating products: ${error.message}`, {
                    error,
                });
                throw new Error(`[updateProducts] Error updating products: ${error.message}`);
            } else {
                logger.error('[updateProducts] Error:', error);
                throw new Error(`[updateProducts] Unknown error occurred`);
            }
        }
    }
}
